import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { lastValueFrom } from "rxjs";
import { CacheService } from "./cache.service";

/**
 * Resolves feature icon URLs (home tiles, sidebar, footer) to ready-to-use
 * <img> sources, and keeps them available synchronously.
 *
 * CacheService reads on native are async and comparatively slow (bridge file
 * read + LZ decompress + JSON parse per icon), so icons used to render as
 * broken images for a moment on every launch. Resolved sources are kept in
 * memory for the session and, on native, mirrored to localStorage so a cold
 * start can paint them on the very first frame. Each icon is still re-read
 * from CacheService once per session so an updated icon replaces a stale one.
 */
@Injectable({ providedIn: "root" })
export class IconCacheService {
  private readonly storageKey = "icon-src-cache";
  private readonly persist = Capacitor.getPlatform() !== "web";
  // Skip persisting unusually large icons to protect localStorage quota
  private readonly maxPersistedLength = 200_000;

  private srcs: { [url: string]: string } = {};
  private revalidated = new Set<string>();
  private pending = new Map<string, Promise<string>>();
  private lastAttempt = new Map<string, number>();
  private readonly retryMs = 15_000;
  private persistTimer: any = null;

  constructor(private cacheService: CacheService) {
    if (!this.persist) return;
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (raw) this.srcs = JSON.parse(raw) || {};
    } catch (e) {
      this.srcs = {};
    }
  }

  /**
   * Synchronous lookup for templates. Returns the best known source (possibly
   * from a previous session) or "" if not resolved yet; either way kicks off
   * a one-time background read so the value is current for this session.
   */
  get(url: string): string {
    if (!this.revalidated.has(url)) {
      // Templates call this every change detection - only retry a failed
      // read (e.g. offline before the icon was ever downloaded) periodically
      const last = this.lastAttempt.get(url) || 0;
      if (!this.pending.has(url) && Date.now() - last > this.retryMs) {
        this.resolve(url);
      }
    }
    return this.srcs[url] || "";
  }

  resolve(url: string): Promise<string> {
    if (this.pending.has(url)) return this.pending.get(url)!;
    this.lastAttempt.set(url, Date.now());

    const promise = lastValueFrom(this.cacheService.getResource(url, true))
      .then((res) => {
        const src = this.toSrc(res?.data);
        if (src) {
          this.revalidated.add(url);
          if (this.srcs[url] !== src) {
            this.srcs[url] = src;
            this.schedulePersist();
          }
        }
        return src || this.srcs[url] || "";
      })
      .catch(() => this.srcs[url] || "")
      .finally(() => this.pending.delete(url));

    this.pending.set(url, promise);
    return promise;
  }

  /** Normalise cached icon data to something <img src> can display */
  private toSrc(data: any): string {
    if (typeof data !== "string" || !data) return "";
    const trimmed = data.trim();
    if (trimmed.startsWith("data:") || /^https?:\/\//.test(trimmed)) {
      return trimmed;
    }
    if (trimmed.startsWith("<svg") || trimmed.startsWith("<?xml")) {
      return "data:image/svg+xml;utf8," + encodeURIComponent(trimmed);
    }
    return trimmed;
  }

  private schedulePersist(): void {
    if (!this.persist || this.persistTimer) return;
    // Batch the burst of writes that happens when a screen resolves its icons
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      try {
        const small: { [url: string]: string } = {};
        for (const [url, src] of Object.entries(this.srcs)) {
          if (src.length <= this.maxPersistedLength) small[url] = src;
        }
        localStorage.setItem(this.storageKey, JSON.stringify(small));
      } catch (e) {
        // Quota or storage unavailable - icons still work from CacheService
      }
    }, 500);
  }
}
